import { ApolloClient, ApolloLink, CombinedGraphQLErrors, HttpLink, InMemoryCache, ServerError } from "@apollo/client";
import { ApolloProvider } from "@apollo/client/react";
import { useState } from "react";
import { ErrorBoundary, useErrorBoundary } from "react-error-boundary";
import { BrowserRouter, Route, Routes } from "react-router-dom";
import { maintenanceRoutes, routes } from "./AppRouting";

import { SetContextLink } from "@apollo/client/link/context";
import { ErrorLink } from "@apollo/client/link/error";
import { ConfigProvider } from "antd";
import axios from "axios";
import ToastDefaultContainer from "components/Alert/ToastDefaultContainer";
import CheckMaintenance from "components/check-maintenance/CheckMaintenance";
import Loader from "components/Loader";
import SignalR from "components/signal-r/SignalR";
import { AppContextProvider } from "helpers/contexts/AppContext";
import { withLayout } from "helpers/hoc/withLayout";
import { useIdleTimer } from "react-idle-timer";
import Loadable from "react-loadable";
import { AuthService } from "services";
import AuthenticationService from "services/AuthenticationService";
import { useChunkErrorRecovery } from "helpers/hooks/useChunkErrorRecovery";

const loading = () => <Loader />;

declare global {
  interface Window {
    REACT_APP_GRAPHQL_URL?: null;
    REACT_APP_IDENTITY_URL?: null;
    REACT_APP_CMS_URL?: null;
    REACT_APP_CMS_API_URL?: null;
    REACT_APP_API_GATEWAY_URL?: null;
    PBI_LIVESTOCK_ACTIVITY_WORKSPACE_ID?: null;
    PBI_LIVESTOCK_ACTIVITY_REPORT_ID?: null;
    PBI_CMS_ACTIVITY_WORKSPACE_ID?: null;
    PBI_CMS_ACTIVITY_REPORT_ID?: null;
    PBI_KILL_DATA_WORKSPACE_ID?: null;
    PBI_KILL_DATA_REPORT_ID?: null;
    PBI_MARKETING_PREFERENCES_WORKSPACE_ID?: null;
    PBI_MARKETING_PREFERENCES_REPORT_ID?: null;
    PBI_PGA_FORM_WORKSPACE_ID?: null;
    PBI_PGA_FORM_REPORT_ID?: null;
  }
}

type GraphQlGatewayResponse = {
  ResponseCode?: number;
  ErrorMessage?: string;
};

axios.interceptors.request.use(async (config) => {
  const token = await AuthService.getAccessToken();
  config.headers.authorization = token ? `Bearer ${token}` : "";
  
  return config;
});

const graphQLLink = new HttpLink({
  uri: window.REACT_APP_GRAPHQL_URL + "/graphql",
});

const authLink = new SetContextLink(async (prevContext, operation) => {
  const token = await AuthService.getAccessToken();
  return {
    headers: {
      ...prevContext.headers,
      authorization: token ? `Bearer ${token}` : "",
    },
  };
});

const errorLink = new ErrorLink(({ error, operation }) => {
  const domain = window.location.origin;

  if (ServerError.is(error)) {
    try {
      const body = JSON.parse(error.bodyText) as GraphQlGatewayResponse;
      
      if (body.ResponseCode === 401) {
        if (operation.operationName === "getMaintenanceStatus") {
          AuthenticationService.signOut().then(() => {
            localStorage.clear();
            window.location.href = window.REACT_APP_CMS_URL;
          });
        } else {
          AuthenticationService.signOut().then(() => {
            localStorage.clear();
            window.location.href = domain;
          });
        }
      }
    } catch (e) {
      // Ignore JSON parse error
    }
  }

  if (CombinedGraphQLErrors.is(error)) {
    for (let err of error.errors) {
      switch (err.extensions.code) {
        case "AUTH_NOT_AUTHORIZED": {
          if (!err.path.includes("getTokenForNotificationHub")) {
            AuthenticationService.signOut().then(() => {
              localStorage.clear();
              window.location.href = domain;
            });
          }
          break;
        }
      }
    }
  }
});

const client = new ApolloClient({
  link: ApolloLink.from([errorLink, authLink.concat(graphQLLink)]),
  cache: new InMemoryCache(),
});

const AuthLayout = Loadable({
  loader: () => import("components/layout/AuthLayout"),
  render(loaded, props) {
    let Component = loaded.default;
    return <Component {...props} />;
  },
  loading,
});

const NonAuthLayout = Loadable({
  loader: () => import("components/layout/NonAuthLayout"),
  render(loaded, props) {
    let Component = loaded.default;
    return <Component {...props} />;
  },
  loading,
});

const App = () => {
  const [maintenance, setMaintenance] = useState(false);
  const [loadingPage, setLoadingPage] = useState(true);

  const handleOnIdle = () => {
    AuthService.signOut();
  };

  useIdleTimer({
    timeout: 1000 * 60 * 30,
    onIdle: handleOnIdle,
    debounce: 500,
  });

  function ErrorFallback({ error }: { error: Error }) {
    const { resetBoundary } = useErrorBoundary();

    return (
      <div role="alert">
        <p>Something went wrong:</p>
        <pre style={{ color: "red" }}>{error.message}</pre>
        <button onClick={resetBoundary}>Try again</button>
      </div>
    );
  }

  const wrapLayout = (route: any) => {
      const Wrapped = withLayout(
        route.useAuthLayout ? AuthLayout : NonAuthLayout,
        route,
        loading
      )(route.component);

      return {
        ...route,
        element: <Wrapped />
      };
    }

  const preparedMaintenanceRoutes = maintenanceRoutes.map(r => wrapLayout(r));
  const prepareRoutes = routes.map((r) => wrapLayout(r));
  const { handleChunkError } = useChunkErrorRecovery();
  return (
    <>
      <ErrorBoundary FallbackComponent={ErrorFallback} onError={handleChunkError}>
        <BrowserRouter>
          <AppContextProvider>
            <ApolloProvider client={client}>
              <ConfigProvider
                theme={{
                  token: {
                    fontFamily: "Ubuntu",
                    colorPrimary: "#0a12bf",
                    fontSize: 16,
                    colorPrimaryHover: "#03002e",
                    colorError: "#ff0000",
                    borderRadius: 0,
                  },
                  components: {
                    Input: { borderRadius: 0 },
                    DatePicker: { borderRadius: 0 },
                    Button: { borderRadius: 0 },
                    Radio: {
                      colorBorder: "#000000",
                      colorPrimary: "black",
                    },
                  },
                }}>
                <SignalR />
                <CheckMaintenance setMaintenance={setMaintenance} setLoadingPage={setLoadingPage} />
                {loadingPage && <></>}
                {!loadingPage && (
                  <>
                    {maintenance ? (
                      <Routes>
                        {preparedMaintenanceRoutes.map((route, index) => {
                          return (
                            <Route
                              key={index}
                              path={route.path}
                              element={route.element}
                            />
                          );
                        })}
                      </Routes>
                    ) : (
                      <Routes>
                        {prepareRoutes.map((route, index) => {
                          return (
                            <Route
                              key={index}
                              path={route.path}
                              element={route.element}
                            />
                          );
                        })}
                      </Routes>
                    )}
                  </>
                )}
                <ToastDefaultContainer />
              </ConfigProvider>
            </ApolloProvider>
          </AppContextProvider>
        </BrowserRouter>
      </ErrorBoundary>
    </>
  );
};

export default App;
